import styled from '@emotion/styled';
import { Table, Tooltip } from 'antd';
import { orderBy } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FiRefreshCcw } from 'react-icons/fi';
import { PulseLoader } from 'react-spinners';
import { TitreEntity } from '../../../../../entities/titre.entity';
import { PRIMARY, YELLOW } from '../../../../../shared/colors';
import { getMovers } from '../../../network';

const Container = styled.div`
  color: var(--mt-text);
  height: 100%;
  box-sizing: border-box;
  padding: 8px 10px 10px;
  overflow-x: auto;
  overflow-y: auto;
  font-family: Poppins-R;

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-family: Poppins-B;
    font-size: 11px;
    font-weight: normal;
    text-transform: uppercase;
  }

  .monitor-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
  }

  .monitor-title {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .monitor-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .refresh-icon {
    display: inline-flex;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--mt-border);
    border-radius: 999px;
    background-color: var(--mt-surface);
    cursor: pointer;
  }

  .empty-state {
    display: grid;
    min-height: 180px;
    place-items: center;
    color: var(--mt-muted);
    border: 1px dashed var(--mt-border);
    border-radius: 8px;
    background-color: var(--mt-surface);
  }

  .odd-row {
    background-color: var(--mt-row-odd);
    color: var(--mt-table-text);
  }

  .even-row {
    background-color: var(--mt-row-even);
    color: var(--mt-table-text);
  }
`;

export const Movers = ({
  getTitre,
}: {
  getTitre: (b: TitreEntity, ope: '1' | '2') => void;
}) => {
  const [movers, setMovers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    setIsLoading(true);
    getMovers().then(({ data }) => {
      setMovers(orderBy(data, 'count', 'desc'));
      setIsLoading(false);
    });
  };

  return (
    <Container>
      <div
        className='monitor-toolbar'
      >
        <span className='monitor-title'>Titres les plus actifs</span>
        <div className='monitor-actions'>
          <PulseLoader size={5} margin={2} color={YELLOW} loading={isLoading} />
          <Tooltip title='Rafraichir' placement='leftTop' color={YELLOW}>
            <span className='refresh-icon' onClick={fetchData}>
              <FiRefreshCcw />
            </span>
          </Tooltip>
        </div>
      </div>
      {movers.length === 0 ? (
        <div className='empty-state'>Aucun mover disponible</div>
      ) : (
        <Table
          columns={COLUMNS}
          dataSource={movers}
          rowSelection={{
            type: 'radio',
            onChange: (selectedRowKeys, selectedRows) => {
              getTitre(selectedRows[0], '1');
            },
          }}
          rowClassName={(row, index) =>
            index % 2 === 0 ? 'even-row' : 'odd-row'
          }
          rowKey='id'
          size='small'
          style={{ zoom: 0.8 }}
          pagination={false}
          showSorterTooltip={false}
        />
      )}
    </Container>
  );
};

const COLUMNS = [
  {
    title: 'Pays',
    dataIndex: 'pays',
    sorter: (a: TitreEntity, b: TitreEntity) => a.pays.localeCompare(b.pays),
  },
  {
    title: 'Type',
    dataIndex: 'instrument',
    sorter: (a: TitreEntity, b: TitreEntity) =>
      a.instrument.localeCompare(b.instrument),
  },
  {
    title: 'CODE',
    dataIndex: 'code',
    sorter: (a: TitreEntity, b: TitreEntity) => a.code.localeCompare(b.code),
    render: (cell: any, row: TitreEntity) => (
      <div
        style={{
          width: 120,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {row.code}
      </div>
    ),
  },
  {
    title: 'Date de maturation',
    dataIndex: 'dateMaturite',
    render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
    sorter: (a: TitreEntity, b: TitreEntity) =>
      a.dateMaturite.localeCompare(b.dateMaturite),
  },
  {
    title: 'Coupon',
    dataIndex: 'tauxMarginal',
    sorter: (a: TitreEntity, b: TitreEntity) => a.tauxMarginal - b.tauxMarginal,
    render: (cell: any, row: TitreEntity) => <>{Number(row.tauxMarginal)}</>,
  },
  {
    title: 'Mover',
    dataIndex: 'count',
    sorter: (a: any, b: any) => a.count - b.count,
  },
];
